import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, Button, IconButton, useResponsiveValue} from '@primer/react'
import {useMemo, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard'
import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import type {OptionConfig} from '../OptionConfig'
import {noop} from '@github-ui/noop'

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  metadataPaneTrigger: JSX.Element
  isSticky: boolean
  setIsIssueTitleEditActive?: (isActive: boolean) => void
}

type HeaderMenuProps = {
  headerMenuData: HeaderMenu$key
} & HeaderMenuBaseProps

export function HeaderMenu({
  headerMenuData,
  isSticky,
  setIsIssueTitleEditActive,
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    commentBoxConfig,
    singleKeyShortcutsEnabled,
    useViewportQueries,
  },
}: HeaderMenuProps) {
  const {viewerCanUpdateNext, url, repository} = useFragment(
    graphql`
      fragment HeaderMenu on Issue {
        id
        viewerCanUpdateNext
        repository {
          id
          name
          owner {
            login
          }
          isArchived
        }
        url
      }
    `,
    headerMenuData,
  )

  const isNarrow = useResponsiveValue({narrow: true}, false)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const scopedRepository = {
    id: repository.id,
    name: repository.name,
    owner: repository.owner.login,
  }

  const editIssueTitleButton = useMemo(
    () =>
      viewerCanUpdateNext ? (
        <Button
          data-testid={TEST_IDS.editIssueTitleButton}
          aria-label={LABELS.editTitle}
          onClick={() => setIsIssueTitleEditActive && setIsIssueTitleEditActive(true)}
          size="medium"
        >
          {BUTTON_LABELS.editTitle}
        </Button>
      ) : null,
    [setIsIssueTitleEditActive, viewerCanUpdateNext],
  )

  return (
    <>
      <Box sx={{display: 'flex', gap: 1, flexDirection: 'row', flexGrow: 1, justifyContent: 'end'}}>
        {!isSticky && editIssueTitleButton}
        <Box
          sx={{flexGrow: useViewportQueries ? ['1', '1', '0', '0'] : isNarrow ? '1' : '0', flexBasis: 'auto', ml: 1}}
        >
          {showIssueCreateButton && !isSticky && !repository.isArchived && (
            <CreateIssueButton
              label={BUTTON_LABELS.new}
              navigate={navigate}
              optionConfig={{
                scopedRepository,
                showFullScreenButton: true,
                // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
                navigateToFullScreenOnTemplateChoice: navigate !== noop && scopedRepository !== null,
                singleKeyShortcutsEnabled,
                useMonospaceFont: commentBoxConfig?.useMonospaceFont,
                pasteUrlsAsPlainText: commentBoxConfig?.pasteUrlsAsPlainText,
              }}
            />
          )}
        </Box>
        <CopyToClipboardButton
          textToCopy={url}
          ariaLabel={BUTTON_LABELS.copyIssueLink}
          icon={CopyIcon}
          tooltipProps={{direction: 's'}}
        />
        {additionalHeaderActions}
        {customEditMenuEntries && (
          <ActionMenu anchorRef={anchorRef}>
            <ActionMenu.Anchor>
              <IconButton
                variant="invisible"
                sx={{flexShrink: 0}}
                icon={KebabHorizontalIcon}
                aria-label={BUTTON_LABELS.issueActions}
                ref={anchorRef}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
        {onClose && (
          <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
        )}
      </Box>
    </>
  )
}

try{ HeaderMenu.displayName ||= 'HeaderMenu' } catch {}